// src/pages/Grow/Intakes/Components/IntakeStatusMenu.tsx
/**
 * Changes an intake's status once it has been sorted out by other means, for example putting a
 * duplicate back to Pending or marking a withdrawal Cancelled.
 *
 * The Grow twin of `Counselling/Requests/Components/RequestStatusMenu.tsx`, and deliberately identical
 * in behaviour so the two queues are worked the same way.
 *
 * ⚠️ **Approved and Rejected are not ordinary statuses.** `changeStatus` returns **400** for them,
 * because reaching either has side effects a bare status write would skip. They are still **listed**
 * rather than quietly filtered out, because a missing option cannot explain itself: selecting one opens
 * the proper Approve or Reject flow, so the menu maps the whole state space and every entry does the
 * right thing.
 */
import { Box, Button, Loader, Menu, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import type { StatusLookup } from '@/pages/Overview/Hooks/useStatusCounts';
import { INTAKE_STATUS, isWorkflowOwned } from '@/pages/Grow/intakeStatusIds';

export interface IntakeStatusMenuProps {
  statuses: StatusLookup[];
  currentStatusID: number;
  busy?: boolean;
  disabled?: boolean;
  /** Ordinary status change, straight through `changeStatus`. */
  onChangeStatus: (statusID: number) => void;
  /** Selected Approved: open the approve flow instead. */
  onApprove: () => void;
  /** Selected Rejected: open the reject flow instead. */
  onReject: () => void;
}

export default function IntakeStatusMenu({
  statuses,
  currentStatusID,
  busy = false,
  disabled = false,
  onChangeStatus,
  onApprove,
  onReject,
}: IntakeStatusMenuProps) {
  const handleSelect = (statusID: number) => {
    if (statusID === INTAKE_STATUS.Approved) { onApprove(); return; }
    if (statusID === INTAKE_STATUS.Rejected) { onReject(); return; }
    onChangeStatus(statusID);
  };

  return (
    <Menu shadow="md" width={280} position="bottom-end">
      <Menu.Target>
        <Button
          variant="default"
          rightSection={busy ? <Loader size={14} /> : <IconChevronDown size={14} />}
          disabled={disabled || busy || statuses.length === 0}
        >
          Change status
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {statuses.map((s) => {
          const isCurrent = s.statusID === currentStatusID;
          const workflow = isWorkflowOwned(s.statusID);
          return (
            <Menu.Item
              key={s.statusID}
              disabled={isCurrent}
              onClick={() => handleSelect(s.statusID)}
              leftSection={
                <Box
                  w={10}
                  h={10}
                  style={{ borderRadius: '50%', backgroundColor: s.color || 'var(--mantine-color-gray-5)' }}
                />
              }
              rightSection={workflow
                ? <Text size="xs" c="dimmed">opens flow</Text>
                : undefined}
            >
              {s.name ?? `Status ${s.statusID}`}
            </Menu.Item>
          );
        })}
        <Menu.Divider />
        <Menu.Label>
          Approving or rejecting opens the full step, so the registration is created and the applicant
          is told.
        </Menu.Label>
      </Menu.Dropdown>
    </Menu>
  );
}
